import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Edit } from 'lucide-react';

const SERVICE_COLORS = {
  'GST Returns': 'bg-emerald-100 text-emerald-700',
  'Income Tax': 'bg-blue-100 text-blue-700',
  'TDS Returns': 'bg-violet-100 text-violet-700',
  'Audit': 'bg-amber-100 text-amber-700',
  'ROC Compliance': 'bg-rose-100 text-rose-700',
  'Advisory': 'bg-cyan-100 text-cyan-700',
};

export const ClientViewDialog = ({ open, onOpenChange, client, canManage, onEdit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      {client && (
        <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                {client.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <span>{client.name}</span>
                {client.company_name && <p className="text-sm font-normal text-muted-foreground">{client.company_name}</p>}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {client.serial_no && (
              <div>
                <p className="text-xs text-muted-foreground">Genius S.No.</p>
                <p className="text-sm font-medium font-mono">{client.serial_no}</p>
              </div>
            )}
            {(client.legal_status || client.dob_doi) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Status (Legal)</p>
                  <p className="text-sm font-medium">{client.legal_status || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">DOB / DOI</p>
                  <p className="text-sm font-medium">{client.dob_doi || '-'}</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium">{client.email || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Mobile</p>
                <p className="text-sm font-medium">{client.phone || '-'}</p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">PAN</p>
                <p className="text-sm font-medium font-mono">{client.pan || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">TAN</p>
                <p className="text-sm font-medium font-mono">{client.tan || '-'}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1.5">GSTIN(s)</p>
              {client.gstins?.length > 0 ? (
                <div className="space-y-1">
                  {client.gstins.map((g, i) => {
                    const num = typeof g === 'string' ? g : g.number;
                    const cat = typeof g === 'string' ? '' : g.category;
                    const reg = typeof g === 'object' ? g.registration_date : null;
                    const canc = typeof g === 'object' ? g.cancellation_date : null;
                    return (
                      <div key={num || `view-gstin-${i}`} className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary" className="font-mono text-xs">{num}</Badge>
                        {cat && <span className="text-[10px] text-muted-foreground">({cat})</span>}
                        {reg && <span className="text-[10px] text-muted-foreground">Regd: {reg}</span>}
                        {canc && <span className="text-[10px] text-red-500 font-medium">Cancelled: {canc}</span>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">-</p>
              )}
            </div>

            <Separator />

            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Services</p>
              {client.services?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {client.services.map(s => (
                    <Badge key={s} variant="secondary" className={`text-xs ${SERVICE_COLORS[s] || ''}`}>{s}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No services assigned</p>
              )}
            </div>

            {client.address && (
              <>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm">{client.address}</p>
                </div>
              </>
            )}

            {client.tags?.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Classification Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {client.tags.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                  </div>
                </div>
              </>
            )}

            {client.notes && (
              <>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Notes & Remarks</p>
                  <p className="text-sm whitespace-pre-wrap bg-muted/30 p-3 rounded-md">{client.notes}</p>
                </div>
              </>
            )}

            {canManage && (
              <Button variant="outline" className="w-full" onClick={() => { onOpenChange(false); onEdit(client); }}>
                <Edit className="h-4 w-4 mr-2" /> Edit Client
              </Button>
            )}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
